import React, {useEffect, useState} from "react";
import {useNavigate} from "react-router";
import {useTranslation} from "react-i18next";
import Api from "../../../services/Api";
import {getLanguage} from "../../../lang/helpers";
import {SurveyQuestionInterface, SurveySectionInterface} from "../../SurveyResult/interfaces";
import CustomButton from "../../../components/CustomButton";
import RadioButton from "../../../components/RadioButton";
import SurveyBadge from "../../../components/Badges/SurveyBadge";
import TwoBars from "../../../components/Bars/TwoBars";
import CheckBox from "../../../components/Boxes/CheckBox";
import ButtonMenu from "../../../components/TabMenu/ButtonMenu";
import RadioBox from "../../../components/Boxes/RadioBox";
import {translateName} from "../../../helpers/translateMap";

interface Props {
  surveyId: string;
  update: Function;
}


const VisionStatementDrafts = (props: Props) => {
  const api = Api.getInstance();
  const navigate = useNavigate();
  const {t, i18n} = useTranslation(); // i18n translation hook
  const lang = getLanguage(i18n.language);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState("");
  const [categoryName, setCategoryName] = useState<string>("");
  const [questions, setQuestions] = useState<Array<SurveyQuestionInterface>>([]);
  const [sections, setSections] = useState<Array<SurveySectionInterface>>([]);
  const [selectedSection, setSelectedSection] = useState("");
  const [selectedView, setSelectedView] = useState("suggestion");
  const [selectedQuestions, setSelectedQuestions] = useState<Array<string>>([]);
  const [answerOptions, setAnswerOptions] = useState(5);
  

  useEffect(() => {
    getData();
  }, [])

  useEffect(() => {
    setSelectedSection("");
    setSelectedQuestions([]);
  }, [selectedView]);

  const getData = () => {
    api.get("client/vision_statement_drafts/" + props.surveyId).then(response => {
      if (response.json.status === "ok") {
        setCategoryName(response.json.categoryName);
        setQuestions(response.json.questions);
        setSections(response.json.sections);
        setAnswerOptions(response.json.answerOptions);
      } else if (response.json.error) {
        setError(response.json.error);
      } else {
        setError("Unknown error");
      }
      setLoading(false);
    });
  }

  const saveCustomGoal = () => {
    const data = {
      selectedQuestionIds: selectedQuestions,
    }
    api.post("client/vision_statement_create_manual/" + props.surveyId, data).then(response => {
      if (response.json.status === "ok") {
        navigate("/dev-plan/long-term-goal/" + response.json.visionStatementId);
      }
    })
  }

  if (loading) {
    return (<div className="mt-4">Loading...</div>);
  }

  if (error.length > 0) {
    return (<div className="mt-4">Error: {error}</div>);
  }

  const selectSection = (section: SurveySectionInterface) => {
    setSelectedSection(section.id);
    let _selectedQuestions: Array<string> = [];
    for (const question of section.questions) {
      _selectedQuestions.push(question.id);
    }
    setSelectedQuestions(_selectedQuestions);
  }

  const renderSections = () => {
    return (
      <>
        <div className="z-body1 mt-4">
          {
            lang === "sv" ? "Välj ett område att utgå från när du skapar ditt mål." : "Select an area to start building your goal around."
          }
        </div>
        {sections.map((section, nr) => (
          <div key={"section_" + section.id} className="mt-4 bg-indigo-800 rounded-md p-4 border-indigo-700 border">
            <div className="flex justify-between">
              <div className="flex">
                <RadioButton
                  id={"draft_" + nr}
                  name={"a_" + nr}
                  label=""
                  value={section.id}
                  checked={section.id === selectedSection}
                  onChange={() => selectSection(section)}
                />
                <div className="z-title1 cursor-pointer" onClick={() => selectSection(section)}>
                  {translateName(section.name, lang, section.translationsName)}
                </div>
              </div>

              <div className="z-body2">Feedback: {section.scoreOther.toFixed(1)}</div>

            </div>

            {
              selectedSection === section.id &&
                <div className="w-full">
                  {
                    section.questions.map((question, i) => {
                      return renderQuestion(question);
                    })
                  }
                </div>
            }

          </div>
        ))}

        <CustomButton buttonType="blueBase" onClick={() => saveCustomGoal()} className="mt-6"
                      disabled={selectedQuestions.length === 0}>
          {t("continue")}
        </CustomButton>
      </>
    )
  }

  const toggleSelectedQuestion = (questionId: string) => {
    if (selectedView === "manual") {
      setSelectedQuestions([questionId]);
    } else {
      let found = selectedQuestions.indexOf(questionId) > -1;
      if (found) {
        const _selectedQuestions: Array<string> = [];
        for (const _qId of selectedQuestions) {
          if (_qId !== questionId) {
            _selectedQuestions.push(_qId);
          }
        }
        setSelectedQuestions(_selectedQuestions);
      } else {
        if (selectedQuestions.length >= 5) {
          alert("You cannot select more than 5 questions")
          return;
        }
        setSelectedQuestions([... selectedQuestions, questionId]);
      }
    }
  }

  const questionDisabled = (questionId: string) => {
    if (selectedQuestions.length < 5) {
      return "";
    }
    if (selectedQuestions.indexOf(questionId) > -1) {
      return "";
    }
    return "opacity-50 pointer-events-none";
  }

  const renderQuestion = (question: SurveyQuestionInterface) => {
    return (
      <div key={"q_" + question.id}
           className="mt-4 bg-indigo-800 rounded-md p-4 border-indigo-700 border">
        <div className="flex items-center gap-x-2">
          <div onClick={(e) => toggleSelectedQuestion(question.id)}
               className={questionDisabled(question.id)}>
            {
              selectedView === "manual" ? (
                <RadioBox checked={selectedQuestions.indexOf(question.id) > -1}/>
              ) : (
                <CheckBox checked={selectedQuestions.indexOf(question.id) > -1}/>
              )
            }
          </div>
          <SurveyBadge lang={lang} type="validated" value={question.scoreSelf}
                       value2={question.scoreOther} max={answerOptions}/>
          <div className="z-badge transparent">{question.focusAreaName}</div>
        </div>

        <div className="flex flex-wrap gap-2 mt-2">
          <div className="z-half">{translateName(question.name, lang, question.translationsName)}</div>

          <div className="z-half">
            <TwoBars value1={question.scoreSelf} value2={question.scoreOther} size="small" max={answerOptions}/>
            <div className="mt-2 flex">
              <div className="text-sm">
                {t("self-assessment")}: <span
                className="z-title2">{question.scoreSelf.toFixed(1)}</span>/{answerOptions}
              </div>
              <div className="ml-4 text-sm">
                Feedback: <span
                className="z-title2">{question.scoreOther === 0 ? "-" : question.scoreOther.toFixed(1)}</span>/{answerOptions}
              </div>
              <div className="ml-4 text-sm">
                Gap: <span
                className="z-title2">{question.scoreOther === 0 ? "-" : Math.abs(question.scoreOther - question.scoreSelf).toFixed(1)}</span>
              </div>
            </div>
          </div>
        </div>
      </div>
    )
  }

  const renderManual = () => {
    return (
      <>

        <div className="overflow-y-scroll mt-2 pb-2" style={{maxHeight: "calc(100dvh - 300px)"}}>
          <div className="z-body1">
            {
              lang === "sv" ? "Välj en fråga att utgå från när du bygger ditt mål." : "Select a question to start building your goal around."
            }
          </div>
          {
            questions.map((question, i) => {
              return renderQuestion(question)
            })
          }
        </div>

        <div className="mt-2 bg-indigo-800 relative">
          <div className="z-gradientback-model"></div>
        </div>

        <CustomButton buttonType="blueBase" onClick={() => saveCustomGoal()} className="mt-2"
                      disabled={selectedQuestions.length === 0}>
          {t("continue")}
        </CustomButton>
      </>
    )
  }

  return (
    <div>
      <ButtonMenu items={[
        {
          title: lang === "sv" ? "Välj från område" : "Select from areas",
          key: "suggestion",
        },
        {
          title: lang === "sv" ? "Välj från frågor" : "Select from questions",
          key: "manual",
        },
      ]} selected={selectedView} selectKey={setSelectedView}/>

      {
        selectedView === "manual" ? renderManual() : renderSections()
      }
    </div>
  )
}

export default VisionStatementDrafts;